// src/components/emailMarketing/SegmentSelector.jsx
import React, { useState, useEffect } from 'react';
import emailMarketingService from '../../helpers/emailMarketingService';

function SegmentSelector({ marcaId, value, onChange, onRecipientsCountChange }) {
    const [segments, setSegments] = useState({
        rfmSegments: [],
        productSegments: [],
        behavioralSegments: []
    });
    const [loading, setLoading] = useState(true);
    const [selectedFilters, setSelectedFilters] = useState(value || {
        rfmSegments: [],
        productSegments: [],
        behavioralSegments: [],
        tags: [],
        country: '',
        city: '',
        lastActiveDays: ''
    });

    // Sincronizar selectedFilters con value cuando cambia desde el padre
    useEffect(() => {
        if (value) {
            setSelectedFilters({
                rfmSegments: value.rfmSegments || [],
                productSegments: value.productSegments || [],
                behavioralSegments: value.behavioralSegments || [],
                tags: value.tags || [],
                country: value.country || '',
                city: value.city || '',
                lastActiveDays: value.lastActiveDays || ''
            });
        }
    }, [value]);

    useEffect(() => {
        if (marcaId) {
            fetchSegments();
        }
    }, [marcaId]);

    useEffect(() => {
        if (marcaId && selectedFilters) {
            calculateRecipients();
        }
    }, [marcaId, selectedFilters]);

    const fetchSegments = async () => {
        try {
            const data = await emailMarketingService.getAvailableSegments(marcaId);
            setSegments({
                rfmSegments: data.rfmSegments || [],
                productSegments: data.productSegments || [],
                behavioralSegments: data.behavioralSegments || []
            });
        } catch (error) {
            // Error silencioso
        } finally {
            setLoading(false);
        }
    };

    const calculateRecipients = async () => {
        if (!marcaId) return;
        
        try {
            // Usar el método del servicio que calcula usando la misma lógica que el backend
            const count = await emailMarketingService.calculateRecipientsBeforeCreate(marcaId, selectedFilters);
            
            if (onRecipientsCountChange) {
                onRecipientsCountChange(count);
            }
        } catch (error) {
            // Error silencioso
        }
    };

    const handleFilterChange = (filterType, value) => {
        const newFilters = { ...selectedFilters };
        
        if (filterType === 'rfmSegments' || filterType === 'productSegments' || filterType === 'behavioralSegments' || filterType === 'tags') {
            newFilters[filterType] = value;
        } else {
            newFilters[filterType] = value;
        }
        
        setSelectedFilters(newFilters);
        if (onChange) {
            onChange(newFilters);
        }
    };

    const toggleArrayItem = (array, item) => {
        if (array.includes(item)) {
            return array.filter(i => i !== item);
        } else {
            return [...array, item];
        }
    };

    if (loading) {
        return <div className="text-gray-500">Cargando segmentos...</div>;
    }

    // Detectar si hay múltiples tipos de segmentos seleccionados
    const hasMultipleSegmentTypes = () => {
        const types = [];
        if (selectedFilters.rfmSegments?.length > 0) types.push('RFM');
        if (selectedFilters.productSegments?.length > 0) types.push('Productos');
        if (selectedFilters.behavioralSegments?.length > 0) types.push('Comportamiento');
        return types.length > 1;
    };

    return (
        <div className="space-y-6">
            {/* Nota sobre lógica de segmentación */}
            {hasMultipleSegmentTypes() && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
                    <div className="flex items-start">
                        <span className="text-yellow-600 mr-2">⚠️</span>
                        <div className="text-sm text-yellow-800">
                            <p className="font-medium mb-1">Lógica de Segmentación:</p>
                            <p>Has seleccionado múltiples tipos de segmentos. Los clientes deben cumplir <strong>TODOS</strong> los tipos seleccionados (AND lógico entre tipos).</p>
                            <p className="mt-1 text-xs">Dentro del mismo tipo de segmento, se aplica OR lógico (el cliente debe cumplir al menos uno).</p>
                        </div>
                    </div>
                </div>
            )}

            {/* Segmentos RFM */}
            {segments.rfmSegments.length > 0 && (
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Segmentos RFM
                    </label>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                        {segments.rfmSegments.map(segment => (
                            <label key={segment} className="flex items-center">
                                <input
                                    type="checkbox"
                                    checked={selectedFilters.rfmSegments?.includes(segment) || false}
                                    onChange={(e) => {
                                        const newArray = toggleArrayItem(
                                            selectedFilters.rfmSegments || [],
                                            segment
                                        );
                                        handleFilterChange('rfmSegments', newArray);
                                    }}
                                    className="mr-2"
                                />
                                <span className="text-sm">{segment}</span>
                            </label>
                        ))}
                    </div>
                </div>
            )}

            {/* Segmentos de Productos */}
            {segments.productSegments.length > 0 && (
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Segmentos de Productos
                    </label>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                        {segments.productSegments.map(segment => (
                            <label key={segment} className="flex items-center">
                                <input
                                    type="checkbox"
                                    checked={selectedFilters.productSegments?.includes(segment) || false}
                                    onChange={(e) => {
                                        const newArray = toggleArrayItem(
                                            selectedFilters.productSegments || [],
                                            segment
                                        );
                                        handleFilterChange('productSegments', newArray);
                                    }}
                                    className="mr-2"
                                />
                                <span className="text-sm">{segment}</span>
                            </label>
                        ))}
                    </div>
                </div>
            )}

            {/* Segmentos de Comportamiento */}
            {segments.behavioralSegments.length > 0 && (
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Segmentos de Comportamiento
                    </label>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                        {segments.behavioralSegments.map(segment => (
                            <label key={segment} className="flex items-center">
                                <input
                                    type="checkbox"
                                    checked={selectedFilters.behavioralSegments?.includes(segment) || false}
                                    onChange={(e) => {
                                        const newArray = toggleArrayItem(
                                            selectedFilters.behavioralSegments || [],
                                            segment
                                        );
                                        handleFilterChange('behavioralSegments', newArray);
                                    }}
                                    className="mr-2"
                                />
                                <span className="text-sm">{segment}</span>
                            </label>
                        ))}
                    </div>
                </div>
            )}

            {/* Tags */}
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tags (separados por comas)
                </label>
                <input
                    type="text"
                    value={selectedFilters.tags?.join(', ') || ''}
                    onChange={(e) => {
                        const tags = e.target.value
                            .split(',')
                            .map(tag => tag.trim())
                            .filter(tag => tag.length > 0);
                        handleFilterChange('tags', tags);
                    }}
                    className="w-full px-4 py-2 border border-gray-300 rounded"
                    placeholder="vip, newsletter, premium"
                />
            </div>

            {/* Filtros adicionales */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        País
                    </label>
                    <input
                        type="text"
                        value={selectedFilters.country || ''}
                        onChange={(e) => handleFilterChange('country', e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded"
                        placeholder="España"
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Ciudad
                    </label>
                    <input
                        type="text"
                        value={selectedFilters.city || ''}
                        onChange={(e) => handleFilterChange('city', e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded"
                        placeholder="Madrid"
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Última Actividad (días)
                    </label>
                    <input
                        type="number"
                        value={selectedFilters.lastActiveDays || ''}
                        onChange={(e) => handleFilterChange('lastActiveDays', e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded"
                        placeholder="30"
                        min="1"
                    />
                </div>
            </div>

            {/* Información sobre lógica de segmentación */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-4">
                <div className="flex items-start">
                    <span className="text-blue-600 mr-2">ℹ️</span>
                    <div className="text-sm text-blue-800">
                        <p className="font-medium mb-1">Cómo funciona la segmentación:</p>
                        <ul className="list-disc list-inside space-y-1 text-xs">
                            <li><strong>Entre tipos de segmentos (RFM, Productos, Comportamiento):</strong> Se aplica AND lógico (el cliente debe cumplir TODOS los tipos seleccionados).</li>
                            <li><strong>Dentro del mismo tipo:</strong> Se aplica OR lógico (el cliente debe cumplir al menos uno de los segmentos seleccionados).</li>
                            <li><strong>Ejemplo:</strong> Si seleccionas "Campeones" (RFM) y "Compradores Frecuentes" (Productos), solo se incluirán clientes que sean Campeones Y Compradores Frecuentes.</li>
                        </ul>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default SegmentSelector;

